/**
 * Cliente tipado del contrato `/v1` (SDD §8.3) para la web. El token lo
 * entrega `auth.ts`; los errores llegan con el catálogo cerrado de la API y
 * su mensaje ya en español.
 */
import {
  type AskTurn,
  type Origen,
  type CustomTemplate,
  type CustomTemplateInput,
  type TopicTracker,
  type Webhook,
  ERROR_MESSAGES,
  type ClientEvent,
  type ErrorCode,
  type ImportPresignInput,
  type Page,
  type PatchMeInput,
  type PlanId,
  type Project,
  type Recording,
  type RecordingDetail,
  type SearchHit,
  type Share,
  type Subscription,
  type Summary,
  type SummaryCorrection,
  type CorrectionTurn,
  type TemplateId,
  type TranscriptSegment,
  type UsageSummary,
  type User,
  type Invitation,
  type MembershipRole,
  type OrgMember,
  type Visibility,
  type AdminAuditoriaPagina,
  type AdminCostos,
  type AdminDescuento,
  type AdminEquipoFila,
  type AdminFichaUsuario,
  type AdminFiltroUsuarios,
  type AdminNota,
  type AdminOperacion,
  type AdminResumen,
  type AdminUsuariosPagina
} from '@eco/shared'
import { CONFIG } from './config'

export interface OrgSettings {
  retencionDias: number | null
  compartirHabilitado: boolean
  /** Si se acortó la retención: desde cuándo se empieza a borrar. */
  retencionDesde: string | null
}

export interface TaskItem {
  id: string
  descripcion: string
  responsableEtiqueta: string | null
  responsableNombre: string | null
  propia?: boolean
  responsableMencionado: string | null
  fecha: string | null
  inicioMs: number | null
  recordingId: string
  titulo: string
  fechaReunion: string
}

export interface AskAllResult {
  respuesta: string
  citas: {
    recordingId: string
    titulo: string
    fechaInicio: string
    inicioMs: number
    cita: string
  }[]
  reuniones: { id: string; titulo: string; fechaInicio: string }[]
  sinRespuesta: boolean
  preguntasRestantes: number
}

export class EcoApiError extends Error {
  constructor(
    readonly code: ErrorCode | 'network_error',
    message: string,
    readonly status: number,
    readonly details?: unknown
  ) {
    super(message)
    this.name = 'EcoApiError'
  }
}

export interface OrgRef {
  orgId: string
  rol: string
  tipo: string
  nombre: string
}

export interface Me {
  user: User
  orgs: OrgRef[]
  /** Aviso para todas las personas (Administración → Ajustes). */
  aviso?: string | null
}

/** Ajustes → Equipo (GET /team). */
export interface TeamView {
  nombre: string
  miRol: MembershipRole
  miembros: OrgMember[]
  invitaciones: Invitation[]
  asientos: number
  plan: string
}

export interface PublicShareView {
  titulo: string
  fechaInicio: string
  consentimientoDeclarado: boolean
  hablantes: string[]
  nombres: Record<string, string>
  notas: { templateId: TemplateId; version: number; contenido: unknown }[]
  transcripcion: TranscriptSegment[] | null
}

export interface AdminOrgOverview {
  org: { id: string; tipo: string; creadoEn: string; retencionDias: number | null }
  subscription: Subscription | null
  usage: UsageSummary
  costoHoyUsd: number
  recientes: Pick<
    Recording,
    'id' | 'estado' | 'duracionMs' | 'errorCode' | 'advertencias' | 'creadoEn'
  >[]
}

export type TokenProvider = () => Promise<string | null>
type Fetch = typeof fetch

export class Api {
  /** Organización activa (X-Org-Id); null = la personal. */
  orgId: string | null = null
  /** Se llama con cada error de la API (p. ej., para notar una suspensión a mitad de sesión). */
  onError: ((e: EcoApiError) => void) | null = null

  constructor(
    private readonly token: TokenProvider,
    private readonly base = `${CONFIG.apiUrl}/v1`,
    private readonly fetchImpl: Fetch = (...a) => fetch(...a)
  ) {}

  async request<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
    const headers: Record<string, string> = { 'x-client': `web/${CONFIG.version}` }
    if (body !== undefined) headers['content-type'] = 'application/json'
    if (auth && this.orgId) headers['x-org-id'] = this.orgId
    if (auth) {
      const t = await this.token()
      if (!t) throw new EcoApiError('unauthorized', ERROR_MESSAGES.unauthorized, 401)
      headers.authorization = `Bearer ${t}`
    }
    let res: Response
    try {
      res = await this.fetchImpl(`${this.base}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body)
      })
    } catch {
      throw new EcoApiError('network_error', 'No hay conexión con eco. Revisa tu internet.', 0)
    }
    if (res.status === 204) return undefined as T
    if (!res.ok) throw await this.errorDe(res)
    return (await res.json().catch(() => null)) as T
  }

  /** El error en español que manda la API (o el genérico si el cuerpo no es JSON). */
  private async errorDe(res: Response): Promise<EcoApiError> {
    const data = (await res.json().catch(() => null)) as {
      error?: { code: ErrorCode; message: string; details?: unknown }
    } | null
    const e = data?.error
    const err = new EcoApiError(
      e?.code ?? 'internal_error',
      e?.message ?? ERROR_MESSAGES.internal_error,
      res.status,
      e?.details
    )
    this.onError?.(err)
    return err
  }

  // Cuenta
  me = () => this.request<Me>('GET', '/me')
  patchMe = (patch: PatchMeInput) => this.request<User>('PATCH', '/me', patch)
  registrarOrigen = (origen: Origen) =>
    this.request<{ guardado: boolean }>('POST', '/me/origen', origen)
  exportData = () => this.request<{ ok: true }>('POST', '/me/export')
  deleteAccount = () => this.request<{ ok: true }>('DELETE', '/me')
  usage = (orgId: string) => this.request<UsageSummary>('GET', `/orgs/${orgId}/usage`)
  extensionLink = () => this.request<{ hashedToken: string }>('POST', '/auth/extension-link')

  // Grabaciones
  listRecordings = (
    q: {
      projectId?: string
      estado?: 'activo' | 'retenida' | 'todos'
      cursor?: string
      limit?: number
    } = {}
  ) => this.request<Page<Recording>>('GET', `/recordings${qs(q)}`)
  recording = (id: string) => this.request<RecordingDetail>('GET', `/recordings/${id}`)
  patchRecording = (
    id: string,
    patch: {
      titulo?: string
      descripcion?: string | null
      projectId?: string | null
      visibilidad?: Visibility
    }
  ) => this.request<Recording>('PATCH', `/recordings/${id}`, patch)
  deleteRecording = (id: string) => this.request<void>('DELETE', `/recordings/${id}`)
  retryRecording = (id: string) => this.request<Recording>('POST', `/recordings/${id}/retry`)
  renameSpeaker = (id: string, speakerId: number, nombre: string | null) =>
    this.request<unknown>('PATCH', `/recordings/${id}/speakers/${speakerId}`, { nombre })
  mergeSpeakers = (id: string, sourceIds: number[], targetId: number) =>
    this.request<unknown>('POST', `/recordings/${id}/speakers/merge`, { sourceIds, targetId })

  // Pregunta a la IA
  ask = (id: string, pregunta: string, historial: AskTurn[] = []) =>
    this.request<{
      respuesta: string
      citas: { inicioMs: number; cita: string }[]
      sinRespuesta: boolean
      preguntasRestantes: number
    }>('POST', `/recordings/${id}/ask`, { pregunta, historial })

  /** Pregunta a todas las reuniones (o a las de un proyecto). */
  askAll = (pregunta: string, projectId?: string, historial: AskTurn[] = []) =>
    this.request<AskAllResult>(
      'POST',
      '/ask',
      projectId ? { pregunta, projectId, historial } : { pregunta, historial }
    )

  // Notas
  requestSummary = (id: string, templateId: TemplateId, customTemplateId?: string) =>
    this.request<Summary>(
      'POST',
      `/recordings/${id}/summaries`,
      customTemplateId ? { templateId, customTemplateId } : { templateId }
    )

  // Tareas de todas las reuniones
  tasks = (dias = 30, projectId?: string) =>
    this.request<TaskItem[]>('GET', `/tasks${qs({ dias: String(dias), projectId })}`)

  // Enviar las notas por correo
  sendNotes = (id: string, input: { destinatarios: string[]; mensaje?: string }) =>
    this.request<{ enviados: number; fallidos: string[] }>('POST', `/recordings/${id}/send`, input)

  // Privacidad de la organización
  orgSettings = () => this.request<OrgSettings>('GET', '/org/settings')
  patchOrgSettings = (p: { retencionDias?: number | null; compartirHabilitado?: boolean }) =>
    this.request<OrgSettings>('PATCH', '/org/settings', p)

  // Vocabulario de la organización
  vocabulary = () => this.request<{ terminos: string[] }>('GET', '/vocabulary')
  setVocabulary = (terminos: string[]) =>
    this.request<{ terminos: string[] }>('PUT', '/vocabulary', { terminos })

  // Temas a seguir
  trackers = () => this.request<TopicTracker[]>('GET', '/trackers')
  createTracker = (t: { nombre: string; terminos: string[] }) =>
    this.request<TopicTracker>('POST', '/trackers', t)
  deleteTracker = (id: string) => this.request<void>('DELETE', `/trackers/${id}`)

  // Integraciones (webhooks salientes)
  webhooks = () => this.request<Webhook[]>('GET', '/integrations/webhooks')
  createWebhook = (w: { url: string; incluirTranscripcion: boolean }) =>
    this.request<Webhook>('POST', '/integrations/webhooks', w)
  deleteWebhook = (id: string) => this.request<void>('DELETE', `/integrations/webhooks/${id}`)
  testWebhook = (id: string) =>
    this.request<{ ok: boolean; estado: number; error?: string }>(
      'POST',
      `/integrations/webhooks/${id}/test`
    )

  // Plantillas personalizadas
  customTemplates = () => this.request<CustomTemplate[]>('GET', '/templates/custom')
  createCustomTemplate = (t: CustomTemplateInput) =>
    this.request<CustomTemplate>('POST', '/templates/custom', t)
  updateCustomTemplate = (id: string, t: CustomTemplateInput) =>
    this.request<CustomTemplate>('PUT', `/templates/custom/${id}`, t)
  deleteCustomTemplate = (id: string) => this.request<void>('DELETE', `/templates/custom/${id}`)
  editSummary = (id: string, sid: string, contenido: unknown) =>
    this.request<Summary>('PATCH', `/recordings/${id}/summaries/${sid}`, { contenido })
  /** Corrige las notas con lo que dice el usuario; crea una versión editada si cambió algo. */
  correctSummary = (
    id: string,
    sid: string,
    instruccion: string,
    historial: CorrectionTurn[] = []
  ) =>
    this.request<SummaryCorrection>('POST', `/recordings/${id}/summaries/${sid}/corrections`, {
      instruccion,
      historial
    })
  summaryFeedback = (id: string, sid: string, feedback: 'up' | 'down' | null, motivo?: string) =>
    this.request<Summary>('POST', `/recordings/${id}/summaries/${sid}/feedback`, {
      feedback,
      motivo
    })

  // Compartir
  createShare = (
    id: string,
    input: { alcance: Share['alcance']; ocultarNombres: boolean; expiraEnDias: 7 | 30 | 90 | null }
  ) => this.request<{ share: Share; url: string }>('POST', `/recordings/${id}/shares`, input)
  revokeShare = (shareId: string) => this.request<void>('DELETE', `/shares/${shareId}`)
  publicShare = (token: string) =>
    this.request<PublicShareView>(
      'GET',
      `/public/shares/${encodeURIComponent(token)}`,
      undefined,
      false
    )

  // Importar
  presignImport = (input: ImportPresignInput) =>
    this.request<{ recordingId: string; url: string; key: string; expiresInSeconds: number }>(
      'POST',
      '/imports/presign',
      input
    )
  completeImport = (input: {
    fechaInicio?: string
    recordingId: string
    titulo?: string
    projectId?: string | null
    idioma?: 'es' | 'en' | 'pt'
  }) => this.request<{ recording: Recording; decision: string }>('POST', '/imports/complete', input)

  // Búsqueda y proyectos
  search = (q: string, projectId?: string, dias?: number) =>
    this.request<SearchHit[]>('GET', `/search${qs({ q, projectId, dias })}`)
  projects = () => this.request<Project[]>('GET', '/projects')
  createProject = (nombre: string, descripcion: string | null = null) =>
    this.request<Project>('POST', '/projects', { nombre, descripcion })
  patchProject = (
    id: string,
    patch: { nombre?: string; descripcion?: string | null; compartido?: boolean }
  ) => this.request<Project>('PATCH', `/projects/${id}`, patch)
  deleteProject = (id: string) => this.request<void>('DELETE', `/projects/${id}`)
  templates = () =>
    this.request<{ id: TemplateId; nombre: string; descripcion: string }[]>(
      'GET',
      '/templates',
      undefined,
      false
    )

  // Cobro
  checkout = (
    plan: Extract<PlanId, 'personal' | 'equipo'>,
    disparador: 'plan' | 'cuota' | 'retenida' | 'bienvenida' = 'plan',
    asientos?: number
  ) =>
    this.request<{ url?: string; transactionId?: string }>('POST', '/billing/checkout', {
      plan,
      disparador,
      ...(asientos ? { asientos } : {})
    })

  // Plan Equipo
  createTeam = (nombre: string) => this.request<{ orgId: string }>('POST', '/teams', { nombre })
  team = () => this.request<TeamView>('GET', '/team')
  invite = (email: string, rol: 'admin' | 'member') =>
    this.request<Invitation>('POST', '/team/invitations', { email, rol })
  revokeInvitation = (id: string) =>
    this.request<void>('DELETE', `/team/invitations/${encodeURIComponent(id)}`)
  setMemberRole = (userId: string, rol: 'admin' | 'member') =>
    this.request<void>('PATCH', `/team/members/${encodeURIComponent(userId)}`, { rol })
  removeMember = (userId: string) =>
    this.request<void>('DELETE', `/team/members/${encodeURIComponent(userId)}`)
  previewInvitation = (token: string) =>
    this.request<{
      equipo: string
      invitador: string
      email: string
      estado: 'vigente' | 'vencida' | 'usada'
    }>('GET', `/public/invitations/${encodeURIComponent(token)}`, undefined, false)
  transferOwnership = (userId: string) => this.request<void>('POST', '/team/owner', { userId })
  changeSeats = (asientos: number) => this.request<void>('POST', '/team/seats', { asientos })
  acceptInvitation = (token: string) =>
    this.request<{ orgId: string }>('POST', '/invitations/accept', { token })
  billingPortal = () => this.request<{ url: string }>('GET', '/billing/portal')

  // Analítica server-side (lista cerrada; nunca contenido)
  event = (evento: ClientEvent['evento'], props?: ClientEvent['props']) =>
    this.request<void>('POST', '/events', { evento, props }).catch(() => undefined)

  // Operador
  adminOrg = (orgId: string) => this.request<AdminOrgOverview>('GET', `/admin/orgs/${orgId}`)
  adminLookup = (email: string) =>
    this.request<{ userId: string; creadoEn: string; orgs: { orgId: string; tipo: string }[] }>(
      'GET',
      `/admin/lookup${qs({ email })}`
    )
  adminCredit = (orgId: string, minutos: number, motivo: string) =>
    this.request<unknown>('POST', `/admin/orgs/${orgId}/credit`, { minutos, motivo })
  adminPlan = (orgId: string, plan: 'free' | 'personal', periodoFin: string | null) =>
    this.request<unknown>('POST', `/admin/orgs/${orgId}/plan`, { plan, periodoFin })
  adminRetry = (recordingId: string) =>
    this.request<unknown>('POST', `/admin/recordings/${recordingId}/retry`)
  adminSettings = () => this.request<Record<string, unknown>>('GET', '/admin/settings')
  adminPatchSettings = (patch: Record<string, unknown>) =>
    this.request<Record<string, unknown>>('PATCH', '/admin/settings', patch)
  adminCosts = () =>
    this.request<{ desde: string; totalUsd: number; presupuestoUsd: number | null }>(
      'GET',
      '/admin/costs'
    )
  adminHealth = () =>
    this.request<{
      estados: Partial<Record<string, number>>
      webhooks: { apagados: number; conFallos: number }
    }>('GET', '/admin/health')
  adminEmbudo = (dias: number) =>
    this.request<{
      desde: string
      dias: number
      total: { registros: number; activados: number; pagando: number }
      filas: {
        fuente: string
        medio: string
        campana: string
        registros: number
        activados: number
        pagando: number
      }[]
    }>('GET', `/admin/embudo${qs({ dias: String(dias) })}`)

  // Panel de administración (docs/panel-admin.md)
  adminResumen = (dias: number) =>
    this.request<AdminResumen>('GET', `/admin/resumen${qs({ dias })}`)
  adminUsuarios = (f: AdminFiltroUsuarios) =>
    this.request<AdminUsuariosPagina>('GET', `/admin/usuarios${qs({ ...f })}`)
  adminFicha = (userId: string) =>
    this.request<AdminFichaUsuario>('GET', `/admin/usuarios/${userId}`)
  adminSuspender = (userId: string, motivo: string) =>
    this.request<{ ok: true }>('POST', `/admin/usuarios/${userId}/suspender`, { motivo })
  adminReactivar = (userId: string, motivo: string) =>
    this.request<{ ok: true }>('POST', `/admin/usuarios/${userId}/reactivar`, { motivo })
  adminNota = (userId: string, texto: string) =>
    this.request<AdminNota>('POST', `/admin/usuarios/${userId}/notas`, { texto })
  adminBorrarCuenta = (userId: string, confirmacion: string, motivo: string) =>
    this.request<{ ok: true }>('POST', `/admin/usuarios/${userId}/borrar`, {
      confirmacion,
      motivo
    })
  adminCortesia = (orgId: string, dias: number, motivo: string) =>
    this.request<UsageSummary>('POST', `/admin/orgs/${orgId}/cortesia`, { dias, motivo })
  adminEquipos = () => this.request<{ items: AdminEquipoFila[] }>('GET', '/admin/equipos')
  adminOperacion = () => this.request<AdminOperacion>('GET', '/admin/operacion')
  adminCostos = (dias: number) => this.request<AdminCostos>('GET', `/admin/costos${qs({ dias })}`)
  adminAuditoria = (f: {
    accion?: string
    operadorId?: string
    orgId?: string
    limite?: number
    desplazamiento?: number
  }) => this.request<AdminAuditoriaPagina>('GET', `/admin/auditoria${qs({ ...f })}`)
  adminDescuentos = () => this.request<{ items: AdminDescuento[] }>('GET', '/admin/descuentos')
  adminCrearDescuento = (d: {
    codigo: string
    descripcion: string
    porcentaje: number
    meses: number | null
    maxUsos: number | null
    expiraEn: string | null
  }) => this.request<AdminDescuento>('POST', '/admin/descuentos', d)
  adminArchivarDescuento = (id: string) =>
    this.request<{ ok: true }>('POST', `/admin/descuentos/${id}/archivar`, {})

  /** Descarga el CSV de usuarios (con el mismo filtro de la lista) y lo guarda. */
  async adminExportarUsuarios(f: AdminFiltroUsuarios): Promise<void> {
    const t = await this.token()
    if (!t) throw new EcoApiError('unauthorized', ERROR_MESSAGES.unauthorized, 401)
    const res = await this.fetchImpl(`${this.base}/admin/usuarios.csv${qs({ ...f })}`, {
      headers: { authorization: `Bearer ${t}`, 'x-client': `web/${CONFIG.version}` }
    })
    if (!res.ok) throw await this.errorDe(res)
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `eco-usuarios-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
}

/** Query string sin claves vacías. */
export function qs(q: Record<string, string | number | undefined | null>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(q))
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  const s = p.toString()
  return s ? `?${s}` : ''
}

/** Sube un archivo a una URL prefirmada informando el progreso (XHR: fetch no lo expone). */
export function uploadWithProgress(
  url: string,
  file: Blob,
  contentType: string,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('content-type', contentType)
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total)
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new EcoApiError('internal_error', 'No se pudo subir el archivo.', xhr.status))
    xhr.onerror = () =>
      reject(new EcoApiError('network_error', 'Se cortó la conexión al subir.', 0))
    xhr.onabort = () => reject(new EcoApiError('network_error', 'Subida cancelada.', 0))
    signal?.addEventListener('abort', () => xhr.abort())
    xhr.send(file)
  })
}
